import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { RequestBody, ResponseBody } from "@src/packages/openapi/helpers";
import type { paths as TaxDeductionPaths } from "@src/packages/openapi/schema";

type TaxDeductionProfileEndpoint =
  TaxDeductionPaths["/patients/{patientId}/tax-deduction-profile"];
type TaxDeductionReviewsEndpoint =
  TaxDeductionPaths["/patients/{patientId}/tax-deduction-reviews"];
type TaxDeductionReviewEndpoint =
  TaxDeductionPaths["/patients/{patientId}/tax-deduction-reviews/{paymentRecordId}"];
type TaxDeductionCalculationEndpoint =
  TaxDeductionPaths["/patients/{patientId}/tax-deduction-calculation"];

export type TaxDeductionTaxYear =
  TaxDeductionCalculationEndpoint["get"]["parameters"]["query"]["taxYear"];

export type TaxDeductionProfile = NonNullable<
  ResponseBody<TaxDeductionProfileEndpoint["get"]>["data"]
>;
export type SaveTaxDeductionProfilePayload = RequestBody<
  TaxDeductionProfileEndpoint["put"]
>;
export type TaxDeductionReview = NonNullable<
  ResponseBody<TaxDeductionReviewsEndpoint["get"]>["data"]
>[number];
export type SaveTaxDeductionReviewPayload = RequestBody<
  TaxDeductionReviewEndpoint["put"]
>;
export type TaxDeductionCalculation = NonNullable<
  ResponseBody<TaxDeductionCalculationEndpoint["get"]>["data"]
>;

function profileQueryKey(
  patientId: string | null,
  taxYear: TaxDeductionTaxYear,
) {
  return ["tax-deduction-profile", patientId, taxYear] as const;
}

function reviewsQueryKey(
  patientId: string | null,
  taxYear: TaxDeductionTaxYear,
) {
  return ["tax-deduction-reviews", patientId, taxYear] as const;
}

export function taxDeductionCalculationQueryKey(
  patientId: string | null,
  taxYear: TaxDeductionTaxYear,
) {
  return ["tax-deduction-calculation", patientId, taxYear] as const;
}

export function taxDeductionCalculationQueryPrefix(patientId: string | null) {
  return ["tax-deduction-calculation", patientId] as const;
}

// The non-versioned default OpenAPI document (the source the client types are
// generated from) does not model an x-api-version parameter, so the version is
// pinned at runtime through the untyped `headers` request option — the same
// pattern useCycles/usePatient use for versioned reads.
const taxApiVersionHeader = { "x-api-version": "2026-06-17" } as const;

export function useTaxDeductionProfile(taxYear: TaxDeductionTaxYear) {
  const { client, patientId } = useApiClient();
  return useQuery<TaxDeductionProfile | null>({
    queryKey: profileQueryKey(patientId, taxYear),
    enabled: Boolean(patientId),
    queryFn: async () => {
      if (!patientId) return null;
      const { data, error, response } = await client.GET(
        "/patients/{patientId}/tax-deduction-profile",
        {
          headers: taxApiVersionHeader,
          params: {
            path: { patientId },
            query: { taxYear },
          },
        },
      );
      if (error) {
        throwApiError(
          error,
          "We couldn't load your tax profile.",
          "useTaxDeductionProfile",
          response?.status,
        );
      }
      return data?.data ?? null;
    },
  });
}

export function useSaveTaxDeductionProfile(taxYear: TaxDeductionTaxYear) {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const queryKey = profileQueryKey(patientId, taxYear);

  return useMutation({
    mutationFn: async (payload: SaveTaxDeductionProfilePayload) => {
      if (!patientId) throw new Error("No patient context");
      const { data, error, response } = await client.PUT(
        "/patients/{patientId}/tax-deduction-profile",
        {
          headers: taxApiVersionHeader,
          params: { path: { patientId } },
          body: payload,
        },
      );
      if (error || !data?.data) {
        throwApiError(
          error,
          "We couldn't save your tax profile.",
          "useSaveTaxDeductionProfile",
          response?.status,
        );
      }
      return data.data;
    },
    onSuccess: (profile) => {
      queryClient.setQueryData(queryKey, profile);
      void queryClient.invalidateQueries({
        queryKey: taxDeductionCalculationQueryKey(patientId, taxYear),
      });
    },
  });
}

export function useTaxDeductionReviews(taxYear: TaxDeductionTaxYear) {
  const { client, patientId } = useApiClient();
  return useQuery<TaxDeductionReview[]>({
    queryKey: reviewsQueryKey(patientId, taxYear),
    enabled: Boolean(patientId),
    queryFn: async () => {
      if (!patientId) return [];
      const { data, error, response } = await client.GET(
        "/patients/{patientId}/tax-deduction-reviews",
        {
          headers: taxApiVersionHeader,
          params: {
            path: { patientId },
            query: { taxYear },
          },
        },
      );
      if (error) {
        throwApiError(
          error,
          "We couldn't load expense reviews.",
          "useTaxDeductionReviews",
          response?.status,
        );
      }
      return data?.data ?? [];
    },
  });
}

export function useTaxDeductionCalculation(taxYear: TaxDeductionTaxYear) {
  const { client, patientId } = useApiClient();
  return useQuery<TaxDeductionCalculation | null>({
    queryKey: taxDeductionCalculationQueryKey(patientId, taxYear),
    enabled: Boolean(patientId),
    queryFn: async () => {
      if (!patientId) return null;
      const { data, error, response } = await client.GET(
        "/patients/{patientId}/tax-deduction-calculation",
        {
          headers: taxApiVersionHeader,
          params: {
            path: { patientId },
            query: { taxYear },
          },
        },
      );
      if (error) {
        throwApiError(
          error,
          "We couldn't calculate your tax review.",
          "useTaxDeductionCalculation",
          response?.status,
        );
      }
      return data?.data ?? null;
    },
  });
}

export function useSaveTaxDeductionReview(taxYear: TaxDeductionTaxYear) {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const queryKey = reviewsQueryKey(patientId, taxYear);

  return useMutation({
    mutationFn: async ({
      paymentRecordId,
      decision,
    }: {
      paymentRecordId: string;
      decision: SaveTaxDeductionReviewPayload["decision"];
    }) => {
      if (!patientId) throw new Error("No patient context");
      const { data, error, response } = await client.PUT(
        "/patients/{patientId}/tax-deduction-reviews/{paymentRecordId}",
        {
          headers: taxApiVersionHeader,
          params: {
            path: { patientId, paymentRecordId },
          },
          body: { taxYear, decision },
        },
      );
      if (error || !data?.data) {
        throwApiError(
          error,
          "We couldn't save that expense review.",
          "useSaveTaxDeductionReview",
          response?.status,
        );
      }
      return data.data;
    },
    onSuccess: (review) => {
      queryClient.setQueryData<TaxDeductionReview[]>(
        queryKey,
        (current = []) => [
          review,
          ...current.filter(
            (item) => item.paymentRecordId !== review.paymentRecordId,
          ),
        ],
      );
      void queryClient.invalidateQueries({
        queryKey: taxDeductionCalculationQueryKey(patientId, taxYear),
      });
    },
  });
}

export function useDeleteTaxDeductionReview(taxYear: TaxDeductionTaxYear) {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const queryKey = reviewsQueryKey(patientId, taxYear);

  return useMutation({
    mutationFn: async (paymentRecordId: string) => {
      if (!patientId) throw new Error("No patient context");
      const { error, response } = await client.DELETE(
        "/patients/{patientId}/tax-deduction-reviews/{paymentRecordId}",
        {
          headers: taxApiVersionHeader,
          params: {
            path: { patientId, paymentRecordId },
            query: { taxYear },
          },
        },
      );
      if (error) {
        throwApiError(
          error,
          "We couldn't reset that expense review.",
          "useDeleteTaxDeductionReview",
          response?.status,
        );
      }
    },
    onSuccess: (_result, paymentRecordId) => {
      queryClient.setQueryData<TaxDeductionReview[]>(queryKey, (current = []) =>
        current.filter((item) => item.paymentRecordId !== paymentRecordId),
      );
      void queryClient.invalidateQueries({
        queryKey: taxDeductionCalculationQueryKey(patientId, taxYear),
      });
    },
  });
}
